import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ExternalLink, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  listRemarkEvents,
  listRemarks,
  REMARK_STATUSES,
  remarkCategoryLabel,
  remarkDialogPath,
  remarkStatusLabel,
  severityClassName,
  updateRemark,
  type Remark,
  type RemarkEvent,
  type RemarkStatus,
} from '@/entities/remarks';
import { ApiError } from '@/shared/api';
import { cn } from '@/shared/lib/utils';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/shadcn/sheet';
import { PageHeader } from '@/widgets/page-header';
import { RemarksBoard } from '@/widgets/remarks-board';

function describeEvent(event: RemarkEvent): string {
  const payload = event.payload ?? {};
  switch (event.kind) {
    case 'created':
      return 'Created';
    case 'status_changed':
      return `${remarkStatusLabel(String(payload.from ?? ''))} → ${remarkStatusLabel(String(payload.to ?? ''))}`;
    case 'note':
      return String(payload.note ?? '');
    case 'commit': {
      const ref = payload.commit_ref ? String(payload.commit_ref) : '';
      const resolution = payload.resolution ? String(payload.resolution) : '';
      return [ref && `commit ${ref}`, resolution].filter(Boolean).join(' · ');
    }
    default:
      return event.kind;
  }
}

function RemarkDetails({
  remark,
  onStatus,
  busy,
}: {
  remark: Remark;
  onStatus: (status: RemarkStatus) => void;
  busy: boolean;
}) {
  const eventsQuery = useQuery({
    queryKey: ['remark-events', remark.id, remark.updated_at],
    queryFn: () => listRemarkEvents(remark.id),
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="outline" className={cn('font-normal', severityClassName(remark.severity))}>
          {remark.severity}
        </Badge>
        <Badge variant="outline" className="font-normal">
          {remarkCategoryLabel(remark.category)}
        </Badge>
        <Badge variant="secondary" className="font-normal">
          {remarkStatusLabel(remark.status)}
        </Badge>
      </div>

      <p className="text-sm leading-relaxed whitespace-pre-wrap">{remark.body}</p>

      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>Dialog</span>
        <Link
          to={remarkDialogPath(remark)}
          className="inline-flex items-center gap-1 font-mono text-foreground hover:underline"
        >
          {remark.dialog_id.slice(0, 8)} <ExternalLink className="size-3" />
        </Link>
        {remark.message_id ? (
          <>
            <span>Message</span>
            <span className="font-mono">{remark.message_id.slice(0, 8)}</span>
          </>
        ) : null}
        {remark.run_id ? (
          <>
            <span>Run</span>
            <span className="font-mono">{remark.run_id.slice(0, 8)}</span>
          </>
        ) : null}
        {remark.commit_ref ? (
          <>
            <span>Commit</span>
            <span className="font-mono">{remark.commit_ref}</span>
          </>
        ) : null}
        {remark.resolution ? (
          <>
            <span>Resolution</span>
            <span className="whitespace-pre-wrap text-foreground">{remark.resolution}</span>
          </>
        ) : null}
        <span>Remark id</span>
        <span className="font-mono select-all">{remark.id}</span>
      </div>

      <div className="space-y-1.5">
        <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
          Move to
        </p>
        <div className="flex flex-wrap gap-1.5">
          {REMARK_STATUSES.filter((status) => status !== remark.status).map((status) => (
            <Button
              key={status}
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => onStatus(status)}
            >
              {remarkStatusLabel(status)}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
          History
        </p>
        {eventsQuery.isLoading ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : null}
        <ul className="space-y-1.5">
          {(eventsQuery.data ?? []).map((event) => (
            <li
              key={event.id}
              className="rounded-md border border-border/60 bg-muted/30 px-2 py-1.5 text-xs"
            >
              <div className="flex items-center justify-between gap-2 text-muted-foreground">
                <span className="font-mono">{event.actor}</span>
                <span>{new Date(event.created_at).toLocaleString()}</span>
              </div>
              <p className="mt-0.5 text-foreground">{describeEvent(event)}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function RemarksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dialogFilter = searchParams.get('dialog_id');
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const remarksQuery = useQuery({
    queryKey: ['remarks', dialogFilter],
    queryFn: () => listRemarks({ dialog_id: dialogFilter ?? undefined, limit: 500 }),
    refetchInterval: 30_000,
  });

  const remarks = useMemo(() => remarksQuery.data ?? [], [remarksQuery.data]);
  const selected = useMemo(
    () => remarks.find((remark) => remark.id === selectedId) ?? null,
    [remarks, selectedId],
  );

  const moveMutation = useMutation({
    mutationFn: ({ remark, status }: { remark: Remark; status: RemarkStatus }) =>
      updateRemark(remark.id, { status }),
    onMutate: ({ remark, status }) => {
      setBusyIds((prev) => new Set(prev).add(remark.id));
      queryClient.setQueryData<Remark[]>(['remarks', dialogFilter], (prev) =>
        (prev ?? []).map((item) => (item.id === remark.id ? { ...item, status } : item)),
      );
    },
    onError: (error, { remark }) => {
      toast.error(error instanceof ApiError ? error.message : 'Failed to move remark');
      queryClient.setQueryData<Remark[]>(['remarks', dialogFilter], (prev) =>
        (prev ?? []).map((item) => (item.id === remark.id ? remark : item)),
      );
    },
    onSettled: async (_data, _error, { remark }) => {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(remark.id);
        return next;
      });
      await queryClient.invalidateQueries({ queryKey: ['remarks'] });
      await queryClient.invalidateQueries({ queryKey: ['playground-sessions'] });
    },
  });

  const counts = useMemo(() => {
    const open = remarks.filter(
      (remark) => remark.status === 'new' || remark.status === 'in_progress',
    ).length;
    return { total: remarks.length, open };
  }, [remarks]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PageHeader
        title="Remarks"
        description={
          dialogFilter
            ? `Remarks for dialog ${dialogFilter.slice(0, 8)}`
            : `${counts.open} open of ${counts.total}. Drag cards between columns; the local fixing agent moves them to Fixed.`
        }
        actions={
          <div className="flex items-center gap-2">
            {dialogFilter ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  render={<Link to={`/playground/${encodeURIComponent(dialogFilter)}`} />}
                >
                  <ExternalLink className="size-4" />
                  Open dialog
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.delete('dialog_id');
                    setSearchParams(next);
                  }}
                >
                  <X className="size-4" />
                  Clear filter
                </Button>
              </>
            ) : null}
          </div>
        }
      />
      <div className="min-h-0 flex-1 px-4 pb-4">
        {remarksQuery.isLoading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading remarks…
          </div>
        ) : remarksQuery.error ? (
          <p className="py-8 text-sm text-destructive">
            {remarksQuery.error instanceof ApiError
              ? remarksQuery.error.message
              : 'Failed to load remarks'}
          </p>
        ) : (
          <RemarksBoard
            remarks={remarks}
            busyIds={busyIds}
            onOpen={(remark) => setSelectedId(remark.id)}
            onMove={(remark, status) => moveMutation.mutate({ remark, status })}
          />
        )}
      </div>

      <Sheet
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      >
        <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
          {selected ? (
            <>
              <SheetHeader className="pr-12">
                <SheetTitle className="leading-snug">{selected.title}</SheetTitle>
                <SheetDescription>
                  Updated {new Date(selected.updated_at).toLocaleString()}
                </SheetDescription>
              </SheetHeader>
              <RemarkDetails
                remark={selected}
                busy={busyIds.has(selected.id)}
                onStatus={(status) => moveMutation.mutate({ remark: selected, status })}
              />
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
